import React, { useCallback, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { ManageCodersModal } from '../../components/manage-coders-modal/manage-coders-modal';
import { CoderListItemProps } from '../../components/coder-list-item/coder-list-item';
import {
  fetchCodersForQueue,
  addCoderToQueue,
  removeCoderFromQueue,
  closeModal,
  stageAddCoder,
  stageRemoveCoder,
  cancelChanges
} from '../../actions/manage-coders.action';
import { setQueueCoderCount } from '../../actions/queue-priority-order.action';
import {
  selectManageCoders,
  selectHasCoderChanges
} from '../../reducers/manage-coders.reducer';

export const ManageCodersModalContainer: React.FC = () => {
  const dispatch = useDispatch();
  const manageCodersState = useSelector(selectManageCoders);
  const service = useSelector(
    (state: any) => state.ui?.serviceLine?.name as string | undefined
  );

  const {
    isOpen,
    activeQueueId,
    activeQueueName,
    originalAssigned,
    stagedAssigned,
    availableCoders,
    isFetching,
    isSaving,
    fetchError,
    saveError
  } = manageCodersState;

  const hasChanges = selectHasCoderChanges(manageCodersState);

  useEffect(() => {
    if (isOpen && activeQueueId && service) {
      dispatch(
        fetchCodersForQueue({ queueId: activeQueueId, service }) as any
      );
    }
  }, [isOpen, activeQueueId, service, dispatch]);

  const handleSave = useCallback(async () => {
    if (!activeQueueId || !service || !hasChanges) return;

    const originalIds = new Set(originalAssigned.map(c => c.coderId));
    const stagedIds = new Set(stagedAssigned.map(c => c.coderId));

    const additions = stagedAssigned.filter(c => !originalIds.has(c.coderId));
    const removals = originalAssigned.filter(c => !stagedIds.has(c.coderId));

    try {
      const addPromises = additions.map(c =>
        dispatch(
          addCoderToQueue({ queueId: activeQueueId, coderId: c.coderId, service }) as any
        )
      );
      const removePromises = removals.map(c =>
        dispatch(
          removeCoderFromQueue({ queueId: activeQueueId, coderId: c.coderId, service }) as any
        )
      );

      const results = await Promise.all([...addPromises, ...removePromises]);
      const anyRejected = results.some(
        (r: any) => r?.error !== undefined
      );

      if (!anyRejected) {
        // Optimistically sync the queue card's count to the new assigned total. The card reads a
        // cron-cached metric refreshed asynchronously by the backend, so it would otherwise stay
        // stale until the next page load.
        dispatch(
          setQueueCoderCount({
            queueId: activeQueueId,
            coderCount: stagedAssigned.length
          })
        );
        dispatch(closeModal());
      }
    } catch {
      // Errors surface via saveError in state; no additional handling needed.
    }
  }, [
    activeQueueId,
    service,
    hasChanges,
    originalAssigned,
    stagedAssigned,
    dispatch
  ]);

  const handleCancel = useCallback(() => {
    dispatch(cancelChanges());
    dispatch(closeModal());
  }, [dispatch]);

  const handleAddCoder = useCallback(
    (coderId: string) => {
      dispatch(stageAddCoder({ coderId }));
    },
    [dispatch]
  );

  const handleRemoveCoder = useCallback(
    (coderId: string) => {
      dispatch(stageRemoveCoder({ coderId }));
    },
    [dispatch]
  );

  const assignedCoderProps: CoderListItemProps[] = stagedAssigned.map(c => ({
    coderName: c.coderName,
    queueCount: c.queueCount,
    avatarVariant: 'assigned',
    actionVariant: 'remove',
    onAction: () => handleRemoveCoder(c.coderId)
  }));

  const availableCoderProps: CoderListItemProps[] = availableCoders.map(c => ({
    coderName: c.coderName,
    queueCount: c.queueCount,
    avatarVariant: 'available',
    actionVariant: 'add',
    onAction: () => handleAddCoder(c.coderId)
  }));

  const saveDisabled = !hasChanges || isSaving || isFetching;

  return (
    <>
      {fetchError && (
        <div
          className="cmx-manage-coders-modal-container__error"
          data-testid="manage-coders-fetch-error"
          role="alert"
        >
          {fetchError}
        </div>
      )}
      {saveError && (
        <div
          className="cmx-manage-coders-modal-container__error"
          data-testid="manage-coders-save-error"
          role="alert"
        >
          {saveError}
        </div>
      )}
      <ManageCodersModal
        isOpen={isOpen}
        queueName={activeQueueName}
        assignedCoders={assignedCoderProps}
        availableCoders={availableCoderProps}
        onSave={handleSave}
        onCancel={handleCancel}
        saveDisabled={saveDisabled}
      />
    </>
  );
};

ManageCodersModalContainer.displayName = 'ManageCodersModalContainer';
export default ManageCodersModalContainer;
